/**
 * Heuristic auction (bazar) decisions for Armenian Belot bots — local lobby only.
 *
 * @module game/belotArmenian/botAuction
 */

const {
  isStrictlyStrongerBid,
  compareBids,
  TRUMP_NO_TRUMP,
  TRUMP_ALL_TRUMP,
  MIN_OPENING_TENS,
} = require("./auction");
const {
  teamOfSeat,
  RANK_Q,
  RANK_K,
  RANK_10,
  RANK_A,
  RANK_9,
  RANK_J,
} = require("./types");

/** @param {any} prev */
function enumerateStrongerBids(prev) {
  const out = [];
  const trumpChoices = [0, 1, 2, 3, TRUMP_NO_TRUMP, TRUMP_ALL_TRUMP];
  for (let tens = MIN_OPENING_TENS; tens <= 24; tens += 1) {
    for (const trump of trumpChoices) {
      const bid = { tens, trump, kapot: false };
      if (!isStrictlyStrongerBid(bid, prev)) continue;
      out.push(bid);
    }
  }
  for (const trump of trumpChoices) {
    const bid = { tens: 25, trump, kapot: true };
    if (!isStrictlyStrongerBid(bid, prev)) continue;
    out.push(bid);
  }
  return out;
}

/** @param {any} prev */
function pickWeakestStrongerBid(prev) {
  const list = enumerateStrongerBids(prev);
  if (!list.length) return null;
  list.sort((a, b) => compareBids(a, b));
  return list[0];
}

/** @param {number[]} hand */
function suitHasKingQueen(hand, suit) {
  let q = false;
  let k = false;
  for (const c of hand) {
    if (Math.floor(c / 8) !== suit) continue;
    const r = c % 8;
    if (r === RANK_Q) q = true;
    if (r === RANK_K) k = true;
  }
  return q && k;
}

/** @param {number[]} hand */
function suitLength(hand, suit) {
  let n = 0;
  for (const c of hand) {
    if (Math.floor(c / 8) === suit) n += 1;
  }
  return n;
}

/** @param {number[]} hand */
function suitHighValue(hand, suit) {
  let s = 0;
  for (const c of hand) {
    if (Math.floor(c / 8) !== suit) continue;
    const r = c % 8;
    if (r === RANK_A) s += 5;
    else if (r === RANK_10) s += 4;
    else if (r === RANK_K) s += 3;
    else if (r === RANK_Q) s += 2;
    else if (r >= 2 && r <= 3) s += 1;
  }
  return s;
}

/**
 * Rough «очки руки» для торгов: старшие карты по всем мастям (для БК) и суммарная сила по мастям.
 * @param {number[]} hand
 */
function handPlainStrength(hand) {
  let nt = 0;
  const perSuit = [0, 0, 0, 0];
  for (const c of hand) {
    const su = Math.floor(c / 8);
    const r = c % 8;
    let add = 0;
    if (r === RANK_A) {
      add = 5;
      nt += 4;
    } else if (r === RANK_10) {
      add = 4;
      nt += 3;
    } else if (r === RANK_K) {
      add = 3;
      nt += 1;
    } else if (r === RANK_Q) {
      add = 2;
      nt += 1;
    } else if (r === RANK_J || r === RANK_9) add = 1;
    perSuit[su] += add;
  }
  return { perSuit, noTrumpSignal: nt };
}

/**
 * Оценка «насколько моя рука тянет контру» против текущей заявки (чем выше — тем увереннее).
 * @param {number[]} hand
 * @param {any} contractBid
 */
function defenderContraConfidence(hand, contractBid) {
  if (!Array.isArray(hand) || hand.length !== 8 || !contractBid || typeof contractBid !== "object") {
    return 0;
  }
  const { perSuit, noTrumpSignal } = handPlainStrength(hand);
  const total = perSuit[0] + perSuit[1] + perSuit[2] + perSuit[3];
  const tens = Number(contractBid.tens) || 10;
  const trump = contractBid.trump;
  let score = total;
  if (typeof trump === "number" && trump >= 0 && trump <= 3) {
    score += perSuit[trump] * 0.55 + suitLength(hand, trump) * 1.15;
  } else if (trump === TRUMP_NO_TRUMP) {
    score = noTrumpSignal * 1.35 + total * 0.35;
  } else if (trump === TRUMP_ALL_TRUMP) {
    score = total * 1.08;
  }
  if (contractBid.kapot) score -= 7;
  if (tens >= 16) score -= 2.5;
  if (tens >= 19) score -= 3;
  if (tens >= 22) score -= 2;
  return score;
}

/**
 * Контра только при заметной силе защиты; без «случайного удвоения».
 * @param {number[]} hand
 * @param {any} contractBid
 * @param {() => number} rng
 */
function shouldDefenderContra(hand, contractBid, rng) {
  const c = defenderContraConfidence(hand, contractBid);
  if (c < 11.5) return false;
  if (contractBid.kapot && c < 19) return false;
  let p = 0.035;
  if (c >= 14) p += 0.028;
  if (c >= 17) p += 0.032;
  if (c >= 20) p += 0.03;
  if (c >= 23) p += 0.025;
  p = Math.min(0.14, p);
  return rng() < p;
}

/**
 * Реконтра (×4) — очень осторожно; чуть смелее, если сам я автор заявки.
 * @param {number[]} hand
 * @param {number} seat
 * @param {number} bidAuthorSeat
 * @param {() => number} rng
 */
function shouldContractTeamRecontra(hand, seat, bidAuthorSeat, rng) {
  if (!Array.isArray(hand) || hand.length !== 8) return false;
  const { perSuit, noTrumpSignal } = handPlainStrength(hand);
  const total = perSuit[0] + perSuit[1] + perSuit[2] + perSuit[3];
  let trust = total + noTrumpSignal * 0.15;
  if (seat === bidAuthorSeat) trust += 2.2;
  else trust -= 1.8;
  if (trust < 14.5) return false;
  let p = 0.018 + Math.min(0.055, (trust - 14.5) * 0.006);
  if (seat !== bidAuthorSeat && trust < 17.5) p *= 0.45;
  p = Math.min(0.09, p);
  return rng() < p;
}

/**
 * @param {any} state
 * @param {number} seat
 * @param {() => number} rng
 * @returns {{ type: 'PASS'|'BID'|'CONTRA'|'RECONTRA', seat: number, bid?: object } | null}
 */
function pickBotAuctionCommand(state, rng = Math.random) {
  const seat = state.currentTurnSeat;
  if (seat == null || !Number.isInteger(seat)) return null;

  const prev = state.auction?.highestBid ?? null;
  const hand = state.hands?.[seat];
  const hasValidHand = Array.isArray(hand) && hand.length === 8;

  const auc = state.auction;
  if (
    auc &&
    auc.phase === "bidding" &&
    auc.highestBid != null &&
    auc.bidAuthorSeat != null &&
    hasValidHand
  ) {
    const ct = teamOfSeat(auc.bidAuthorSeat);
    const st = teamOfSeat(seat);
    const hb = auc.highestBid;
    if (auc.contraMultiplier === 1 && st !== ct) {
      if (shouldDefenderContra(hand, hb, rng)) {
        return { type: "CONTRA", seat };
      }
    } else if (auc.contraMultiplier === 2 && st === ct) {
      if (shouldContractTeamRecontra(hand, seat, auc.bidAuthorSeat, rng)) {
        return { type: "RECONTRA", seat };
      }
    }
  }

  if (!hasValidHand) {
    if (rng() < 0.42) return { type: "PASS", seat };
    const bid = pickWeakestStrongerBid(prev);
    if (!bid) return { type: "PASS", seat };
    return { type: "BID", seat, bid };
  }

  const { perSuit, noTrumpSignal } = handPlainStrength(hand);
  let bellaSuit = -1;
  for (let s = 0; s <= 3; s += 1) {
    if (suitHasKingQueen(hand, s) && suitLength(hand, s) >= 3) {
      bellaSuit = s;
      break;
    }
  }

  const maxSuit = perSuit.indexOf(Math.max(perSuit[0], perSuit[1], perSuit[2], perSuit[3]));
  const maxVal = perSuit[maxSuit];
  const total = perSuit[0] + perSuit[1] + perSuit[2] + perSuit[3];
  const weak = total < 10 && noTrumpSignal < 6;
  const prevTens = prev && typeof prev === "object" ? Number(prev.tens) || 0 : 0;

  if (weak && rng() < 0.78) {
    return { type: "PASS", seat };
  }

  /* Поднимать сильную заявку соперника без руки — реже. */
  if (prevTens >= 15 && total < 14 && rng() < 0.62) {
    return { type: "PASS", seat };
  }
  if (prevTens >= 18 && total < 16 && rng() < 0.74) {
    return { type: "PASS", seat };
  }

  if (bellaSuit >= 0 && rng() < 0.58) {
    const list = enumerateStrongerBids(prev).filter((b) => b.trump === bellaSuit && !b.kapot);
    if (list.length) {
      list.sort((a, b) => compareBids(a, b));
      const floor = list.find((b) => b.tens >= MIN_OPENING_TENS) || list[0];
      return { type: "BID", seat, bid: floor };
    }
  }

  if (noTrumpSignal >= 10 && rng() < 0.24) {
    const list = enumerateStrongerBids(prev).filter((b) => b.trump === TRUMP_NO_TRUMP && !b.kapot);
    if (list.length) {
      list.sort((a, b) => compareBids(a, b));
      return { type: "BID", seat, bid: list[0] };
    }
  }

  if (noTrumpSignal >= 9 && total >= 14 && rng() < 0.2) {
    const list = enumerateStrongerBids(prev).filter((b) => b.trump === TRUMP_ALL_TRUMP && !b.kapot);
    if (list.length) {
      list.sort((a, b) => compareBids(a, b));
      return { type: "BID", seat, bid: list[0] };
    }
  }

  if (maxVal >= 8 && suitHighValue(hand, maxSuit) >= 6 && rng() < 0.32) {
    const list = enumerateStrongerBids(prev).filter((b) => b.trump === maxSuit && !b.kapot);
    if (list.length) {
      list.sort((a, b) => compareBids(a, b));
      return { type: "BID", seat, bid: list[0] };
    }
  }

  if (rng() < 0.48) {
    return { type: "PASS", seat };
  }
  const bid = pickWeakestStrongerBid(prev);
  if (!bid) return { type: "PASS", seat };
  return { type: "BID", seat, bid };
}

module.exports = {
  pickBotAuctionCommand,
  enumerateStrongerBids,
  pickWeakestStrongerBid,
  handPlainStrength,
  suitHasKingQueen,
  suitLength,
  defenderContraConfidence,
  shouldDefenderContra,
  shouldContractTeamRecontra,
};
